<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <div v-if="label" class="form-label" :class="{ required: isRequired }">{{ label }}</div>

    <div class="btn-group" role="group">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="btn"
        :class="{ 'is-selected': isSelected(option.value) }"
        @click="toggle(option.value)">
        {{ option.label }}
      </button>
    </div>

    <FormErrorList v-if="errors?.length" :errors="errors" class="mt-1 mx-1" />

    <div v-if="$slots.hint" class="hint-text">
      <slot name="hint" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { FormErrorList } from '@prospective/lithium';

defineOptions({ inheritAttrs: false });

export interface ButtonGroupOption {
  value: string | number;
  label?: string;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | (string | number)[];
    options: ButtonGroupOption[];
    type?: 'checkbox' | 'radio';
    label?: string;
    errors?: string[];
    isRequired?: boolean;
    exclusiveOption?: string | number;
  }>(),
  {
    type: 'checkbox',
    errors: () => [],
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string | number | (string | number)[]];
}>();

const isSelected = (value: string | number): boolean => {
  if (props.type === 'radio') return props.modelValue === value;
  return Array.isArray(props.modelValue) && props.modelValue.includes(value);
};

const toggle = (value: string | number) => {
  if (props.type === 'radio') {
    emit('update:modelValue', value);
    return;
  }
  const current = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
  const idx = current.indexOf(value);
  if (props.exclusiveOption !== undefined) {
    if (value === props.exclusiveOption) {
      emit('update:modelValue', idx === -1 ? [props.exclusiveOption] : []);
      return;
    }
    // deselect exclusive option when picking a specific value
    const withoutExclusive = current.filter((v) => v !== props.exclusiveOption);
    const specificIdx = withoutExclusive.indexOf(value);
    if (specificIdx === -1) {
      withoutExclusive.push(value);
    } else {
      withoutExclusive.splice(specificIdx, 1);
    }
    emit('update:modelValue', withoutExclusive);
    return;
  }
  if (idx === -1) {
    current.push(value);
  } else {
    current.splice(idx, 1);
  }
  emit('update:modelValue', current);
};
</script>

<style scoped lang="scss">
.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
  user-select: none;

  &.required::after {
    content: ' *';
    color: #ef4444;
  }
}

.btn-group {
  display: flex;
  flex-wrap: wrap;
}

.btn {
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  height: 40px;
  padding: 10px 14px;
  font-size: 13px;
  font-family: var(--font-brand);
  font-weight: 500;
  background-color: #ffffff;
  color: #344054;
  box-shadow: 0px 1px 2px 0px #1018280d;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: var(--tblr-primary);
    background-color: #f9fafb;
    color: #344054;
  }

  &.is-selected {
    background-color: var(--tw-color-primary);
    border-color: var(--tw-color-primary);
    color: #ffffff;
    box-shadow: none;

    &:hover {
      background-color: #0066d6;
      border-color: #0066d6;
    }
  }

  &:focus-visible {
    outline: 4px solid color-mix(in srgb, var(--tw-color-primary) 10%, transparent);
    outline-offset: 0;
  }
}

.hint-text {
  margin-top: 6px !important;
  font-size: 12px;
  font-weight: 400;
  color: #475467;
}
</style>
